import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Save, Plus } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import {
  SiteContentTab,
  GalleryTab,
  VideosTab,
  AttractionsTab,
  TestimonialsTab,
} from "@/pages/admin/CMS";
import SMTPTab from "@/pages/admin/SMTPTab";

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const map = {
    pending: "bg-[#E2A53D]/15 text-[#9D6B1F]",
    confirmed: "bg-[#4A5D23]/15 text-[#4A5D23]",
    cancelled: "bg-[#B5441E]/15 text-[#B5441E]",
  };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-sm text-xs font-medium ${map[status] || ""}`}>
      {t(`admin.${status}`)}
    </span>
  );
};

const BookingsTab = () => {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } catch (e) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (booking) => {
    try {
      await api.patch(`/admin/bookings/${booking.id}`, {
        status: booking.status,
        admin_notes: booking.admin_notes,
      });
      toast.success("OK");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(t("common.error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("OK");
      load();
    } catch (e) {
      toast.error(t("common.error"));
    }
  };

  if (loading)
    return <div className="text-[#5C4D3C] py-10" data-testid="bookings-loading">{t("common.loading")}</div>;
  if (bookings.length === 0)
    return <div className="text-[#5C4D3C] py-10" data-testid="bookings-empty">{t("admin.no_bookings")}</div>;

  return (
    <div data-testid="bookings-tab" className="overflow-x-auto border border-[#E2D9C8] rounded-sm bg-[#FDFBF7]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("admin.guest")}</TableHead>
            <TableHead>{t("admin.dates")}</TableHead>
            <TableHead>{t("admin.people")}</TableHead>
            <TableHead>{t("booking.room")}</TableHead>
            <TableHead>{t("admin.status")}</TableHead>
            <TableHead>{t("admin.created")}</TableHead>
            <TableHead className="text-end">—</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((b) => (
            <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
              <TableCell>
                <div className="font-medium text-[#2D2824]">{b.full_name}</div>
                <div className="text-xs text-[#5C4D3C]">{b.email}</div>
                <div className="text-xs text-[#5C4D3C] ltr-numbers">{b.phone}</div>
              </TableCell>
              <TableCell className="ltr-numbers text-sm">
                {b.check_in} → {b.check_out}
              </TableCell>
              <TableCell className="ltr-numbers">{b.guests}</TableCell>
              <TableCell className="text-sm">{b.room_name || "—"}</TableCell>
              <TableCell><StatusBadge status={b.status} /></TableCell>
              <TableCell className="text-xs text-[#5C4D3C] ltr-numbers">
                {new Date(b.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-end">
                <div className="flex items-center justify-end gap-2">
                  <Dialog open={editing?.id === b.id} onOpenChange={(o) => !o && setEditing(null)}>
                    <DialogTrigger asChild>
                      <button
                        data-testid={`booking-edit-${b.id}`}
                        onClick={() => setEditing({ ...b })}
                        className="text-xs text-[#B5441E] hover:underline"
                      >
                        ✎
                      </button>
                    </DialogTrigger>
                    {editing && editing.id === b.id && (
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-display text-2xl">
                            {editing.full_name}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>{t("admin.status")}</Label>
                            <Select
                              value={editing.status}
                              onValueChange={(v) => setEditing({ ...editing, status: v })}
                            >
                              <SelectTrigger data-testid="edit-status">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">{t("admin.pending")}</SelectItem>
                                <SelectItem value="confirmed">{t("admin.confirmed")}</SelectItem>
                                <SelectItem value="cancelled">{t("admin.cancelled")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>{t("admin.notes")}</Label>
                            <Textarea
                              data-testid="edit-notes"
                              value={editing.admin_notes || ""}
                              onChange={(e) =>
                                setEditing({ ...editing, admin_notes: e.target.value })
                              }
                              rows={3}
                            />
                          </div>
                          {editing.special_requests && (
                            <div className="text-sm">
                              <div className="text-xs uppercase tracking-[0.2em] text-[#5C4D3C] mb-1">
                                {t("booking.requests")}
                              </div>
                              <div>{editing.special_requests}</div>
                            </div>
                          )}
                          <Button
                            data-testid="edit-save"
                            onClick={() => handleSave(editing)}
                            className="w-full bg-[#B5441E] hover:bg-[#9D3A19] text-white"
                          >
                            <Save size={14} className="me-2" /> {t("admin.save")}
                          </Button>
                        </div>
                      </DialogContent>
                    )}
                  </Dialog>
                  <button
                    data-testid={`booking-delete-${b.id}`}
                    onClick={() => handleDelete(b.id)}
                    className="text-[#B5441E] hover:text-[#9D3A19]"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const emptyRoom = () => ({
  slug: "",
  name: { tr: "", en: "", ar: "" },
  description: { tr: "", en: "", ar: "" },
  price_try: 3000,
  capacity: 2,
  size_m2: 20,
  bed_type: { tr: "", en: "", ar: "" },
  images: [""],
  active: true,
});

const RoomEditor = ({ initial, onSaved }) => {
  const { t } = useLanguage();
  const [r, setR] = useState(initial || emptyRoom());
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...r, images: r.images.filter((x) => x) };
      if (isEdit) {
        await api.patch(`/admin/rooms/${initial.id}`, payload);
      } else {
        await api.post("/admin/rooms", payload);
      }
      toast.success("OK");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const updateLocal = (field, locale, val) => {
    setR((prev) => ({ ...prev, [field]: { ...prev[field], [locale]: val } }));
  };

  return (
    <div className="space-y-4">
      {!isEdit && (
        <div className="space-y-2">
          <Label>Slug</Label>
          <Input
            data-testid="room-slug"
            value={r.slug}
            onChange={(e) => setR({ ...r, slug: e.target.value })}
          />
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        {["tr", "en", "ar"].map((lc) => (
          <div key={`name-${lc}`} className="space-y-2">
            <Label className="uppercase text-xs">Name ({lc})</Label>
            <Input
              data-testid={`room-name-${lc}`}
              value={r.name[lc]}
              onChange={(e) => updateLocal("name", lc, e.target.value)}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {["tr", "en", "ar"].map((lc) => (
          <div key={`desc-${lc}`} className="space-y-2">
            <Label className="uppercase text-xs">Description ({lc})</Label>
            <Textarea
              data-testid={`room-desc-${lc}`}
              value={r.description[lc]}
              onChange={(e) => updateLocal("description", lc, e.target.value)}
              rows={3}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label>{t("admin.price")}</Label>
          <Input
            type="number"
            data-testid="room-price"
            value={r.price_try}
            onChange={(e) => setR({ ...r, price_try: parseInt(e.target.value || "0", 10) })}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("admin.capacity")}</Label>
          <Input
            type="number"
            data-testid="room-capacity"
            value={r.capacity}
            onChange={(e) => setR({ ...r, capacity: parseInt(e.target.value || "0", 10) })}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("admin.size_m2")}</Label>
          <Input
            type="number"
            data-testid="room-size"
            value={r.size_m2}
            onChange={(e) => setR({ ...r, size_m2: parseInt(e.target.value || "0", 10) })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Görseller</Label>
        <div className="grid grid-cols-2 gap-3">
          {r.images.map((img, idx) => (
            <ImageUploader
              key={idx}
              label={`Görsel ${idx + 1}`}
              value={img}
              onChange={(u) =>
                setR({ ...r, images: r.images.map((x, i) => (i === idx ? u : x)) })
              }
              testid={`room-image-${idx}`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="room-image-add"
            onClick={() => setR({ ...r, images: [...r.images, ""] })}
            className="text-xs"
          >
            <Plus size={12} className="me-1" /> Görsel Ekle
          </Button>
          {r.images.length > 1 && (
            <Button
              type="button"
              variant="outline"
              data-testid="room-image-remove"
              onClick={() => setR({ ...r, images: r.images.slice(0, -1) })}
              className="text-xs"
            >
              <Trash2 size={12} className="me-1" /> Son Görseli Kaldır
            </Button>
          )}
        </div>
      </div>
      <Button
        data-testid="room-save"
        onClick={save}
        disabled={saving}
        className="w-full bg-[#B5441E] hover:bg-[#9D3A19] text-white"
      >
        <Save size={14} className="me-2" /> {t("admin.save")}
      </Button>
    </div>
  );
};

const RoomsTab = () => {
  const { t, lang } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rooms");
      setRooms(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/rooms/${id}`);
      toast.success("OK");
      load();
    } catch {
      toast.error(t("common.error"));
    }
  };

  if (loading) return <div className="text-[#5C4D3C] py-10">{t("common.loading")}</div>;

  return (
    <div data-testid="rooms-tab" className="space-y-5">
      <div className="flex justify-end">
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button
              data-testid="rooms-add"
              className="bg-[#B5441E] hover:bg-[#9D3A19] text-white"
            >
              <Plus size={14} className="me-2" /> {t("admin.add_room")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t("admin.add_room")}</DialogTitle>
            </DialogHeader>
            <RoomEditor
              onSaved={() => {
                setOpenNew(false);
                load();
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto border border-[#E2D9C8] rounded-sm bg-[#FDFBF7]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("booking.room")}</TableHead>
              <TableHead>{t("admin.price")}</TableHead>
              <TableHead>{t("admin.capacity")}</TableHead>
              <TableHead>{t("admin.size_m2")}</TableHead>
              <TableHead className="text-end">—</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rooms.map((r) => (
              <TableRow key={r.id} data-testid={`room-row-${r.slug}`}>
                <TableCell>
                  <div className="font-medium">{r.name[lang] || r.name.tr}</div>
                  <div className="text-xs text-[#5C4D3C]">{r.slug}</div>
                </TableCell>
                <TableCell className="ltr-numbers">
                  ₺{r.price_try.toLocaleString("tr-TR")}
                </TableCell>
                <TableCell className="ltr-numbers">{r.capacity}</TableCell>
                <TableCell className="ltr-numbers">{r.size_m2}</TableCell>
                <TableCell className="text-end">
                  <div className="flex items-center justify-end gap-2">
                    <Dialog open={editing?.id === r.id} onOpenChange={(o) => !o && setEditing(null)}>
                      <DialogTrigger asChild>
                        <button
                          data-testid={`room-edit-${r.slug}`}
                          onClick={() => setEditing(r)}
                          className="text-[#B5441E]"
                        >
                          ✎
                        </button>
                      </DialogTrigger>
                      {editing && editing.id === r.id && (
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{editing.name?.tr || editing.slug}</DialogTitle>
                          </DialogHeader>
                          <RoomEditor
                            initial={editing}
                            onSaved={() => {
                              setEditing(null);
                              load();
                            }}
                          />
                        </DialogContent>
                      )}
                    </Dialog>
                    <button
                      data-testid={`room-delete-${r.slug}`}
                      onClick={() => handleDelete(r.id)}
                      className="text-[#B5441E]"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E2D9C8] bg-white">
        <div className="container-fhk flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <a href="/" className="font-display text-xl text-[#2D2824]">
              Fatma Hanım <span className="text-[#B5441E]">Konağı</span>
            </a>
            <span className="text-xs uppercase tracking-[0.2em] text-[#5C4D3C] ms-3">
              {t("nav.admin")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-xs text-[#5C4D3C]">{user?.email}</div>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm text-[#B5441E] hover:text-[#9D3A19]"
            >
              <LogOut size={15} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>
      <main className="container-fhk py-10">
        <Tabs defaultValue="bookings">
          <TabsList data-testid="admin-tabs" className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="bookings" data-testid="tab-bookings">{t("admin.bookings")}</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="tab-rooms">{t("admin.rooms")}</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">İçerik</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Galeri</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Video</TabsTrigger>
            <TabsTrigger value="attractions" data-testid="tab-attractions">Cazibe Merkezleri</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Yorumlar</TabsTrigger>
            <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings"><BookingsTab /></TabsContent>
          <TabsContent value="rooms"><RoomsTab /></TabsContent>
          <TabsContent value="content"><SiteContentTab /></TabsContent>
          <TabsContent value="gallery"><GalleryTab /></TabsContent>
          <TabsContent value="videos"><VideosTab /></TabsContent>
          <TabsContent value="attractions"><AttractionsTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="smtp"><SMTPTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
